import type { ITracerInit } from '@ok/interfaces/tracer/ITracerConfiguration';
import type {
    ITracerError,
    ITracerLog,
    ITracerPerformance,
} from '@apptracer/sdk';
import {
    getTracerModules,
    initTracerErrorAsyncStack,
    initTracerErrorUploader,
    initTracerWebVitalsUploader,
    initTracerPerformanceUploader,
    initTracerSessionUploader
} from '@apptracer/sdk';
import * as logger from 'OK/logger';

enum TracerModule {
    ERROR = 'ERROR',
    LOG = 'LOG',
    PERFORMANCE = 'PERFORMANCE',
    ASYNC = 'ASYNC',
    WEB_VITALS = 'WEB_VITALS'
}

/**
 * Данный сервис содержит все необходимые методы для логирования событий в Tracer. (https://apptracer.ru/doc/about)
 * Подгружается асинхронно, поэтому в res/js/tracerPlaceholder.ts есть заглушки (logQueue, errorQueue, errorKeysQueue),
 * в которые аккумулируются данные от разных приложений.
 * После инициализации, эти данные будут обработаны и применены, а также в window.OK.Tracer поместится весь нижеописанный инстанс.
 * Таким образом, для логирования информации из приложений необходимо вызывать методы модулей прямо из window.OK.Tracer.
 */
class TracerService {
    /** Модуль для работы с ошибками. */
    public static errorModule?: ITracerError;
    /** Модуль для добавления логов. */
    public static logModule?: ITracerLog;
    /** Модуль для работы с перфомансом. */
    public static performanceModule?: ITracerPerformance;

    /** Ручная регистрация ошибки. В интерфейсе Трейсера будет с типом NON_FATAL */
    public static error(error: Error, keys?: Record<string, number | string | boolean>) {
        logger.saveAsClientError(error);

        const errorModule = this.errorModule;

        if (errorModule) {
            errorModule.setErrorKey('currentUrl', window.location.href);
            errorModule.error(error, {keys});
        }
    }

    /**
     * Ключ и значение будут отправлены с каждой ошибкой.
     * В интерфейсе Трейсера доступен поиск ошибок по ключам.
     */
    public static setErrorKey(key: string, value: string | number | boolean) {
        this.errorModule?.setErrorKey(key, value);
    }

    /** Добавляет запись в лог. Логи отправляются в качестве вспомогательной информации с каждой ошибкой. */
    public static log(message: string) {
        this.logModule?.log(message);
    }

    public static initTracerForHead() {
        const {
            log,
            error,
            performance
        } = getTracerModules();

        this.logModule = log;
        this.errorModule = error
        this.performanceModule = performance

        OK.Tracer.tracerAttached(this);
    }

    /** Инициализация, необходимо вызывать один раз на старте приложения. */
    public static init({crashToken, environment, buildNumber, userStatId, userId, modules, ignoreErrors, nonFatalErrors}: ITracerInit) {
        if (!crashToken) {
            OK.logging.logger.error('[tracer] crashToken is required!');
            return;
        }

        const initConfig = {
                versionName: buildNumber || 'dev',
                versionCode: this.convertBuildNumberToNumber(buildNumber),
                appToken: crashToken
            };

        const init_modules = modules || [];
        OK.Tracer.ignoreErrors = ignoreErrors;
        OK.Tracer.nonFatalErrors = nonFatalErrors;

        if (init_modules.includes(TracerModule.ASYNC)) {
            // Патчим промисы для полноты асинхронных стектрейсов в Трейсере
            initTracerErrorAsyncStack();

            OK.logging.info('[tracer] TracerErrorAsync initialized');
        }

        if (init_modules.includes(TracerModule.ERROR)) {
            if (this.errorModule) {
                this.errorModule.setErrorKey("statId", userStatId);
                this.errorModule.setErrorKey("environment", environment);
                this.errorModule.setUserId(String(userId));
            }

            // Инициализация загрузчика сессий для отображения графика Аудитория в Трейсере
            initTracerSessionUploader(initConfig);
            // Инициализация загрузчика ошибок, грузит батчами
            initTracerErrorUploader(initConfig);

            OK.logging.info('[tracer] TracerError initialized');
        }

        if (init_modules.includes(TracerModule.PERFORMANCE)) {
            // Инициализация загрузчика метрик перфоманса
            initTracerPerformanceUploader(initConfig);

            OK.logging.info('[tracer] TracerPerformance initialized');
        }

        if (init_modules.includes(TracerModule.WEB_VITALS)) {
            // Инициализация загрузчика метрик Web Vitals
            // Если загрузчик перфоманса уже есть, будет использован он
            initTracerWebVitalsUploader(initConfig);

            OK.logging.info('[tracer] Web Vitals initialized');
        }
    }

    /**
     * Переводит buildNumber в число: 1.0.976prefix => 1000000976
     * Формула:
     * MAJOR * 1kkk + MINOR * 1kk + PATCH * 1 = RESULT
     * @param buildNumber
     */
    public static convertBuildNumberToNumber = (buildNumber: string): number =>
        buildNumber
            .match(/[\d.]+/)![0]
            .split('.')
            .reduce((acc, num, index) => {
                let currentNum = Number(num);
                switch (index) {
                    case 0:
                        currentNum *= 1_000_000_000;
                        break;
                    case 1:
                        currentNum *= 1_000_000;
                        break;
                    default:
                        break;
                }

                acc += currentNum;

                return acc;
            }, 0);
}

TracerService.initTracerForHead();

export default TracerService;
